import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";

// Leave Type options
const leaveTypeOptions = [
  { id: "annual_leave", name: "Annual Leave" },
  { id: "bereavement_leave", name: "Bereavement (Compassionate) Leave" },
  { id: "community_service_leave", name: "Community Service Leave" },
  { id: "long_service_leave", name: "Long Service Leave" },
  { id: "other_paid_leave", name: "Other Paid Leave" },
  { id: "sick_leave", name: "Sick (Personal/Carer's) Leave" },
  { id: "time_off_in_lieu", name: "Time Off In Lieu" },
  { id: "unpaid_leave_leave", name: "Unpaid Leave - Leave" },
  { id: "unpaid_leave_no_show", name: "Unpaid Leave - No Show" },
  { id: "unpaid_leave_sick", name: "Unpaid Leave - Sick" },
];

// MultiSelectDropdown with portal
function MultiSelectDropdown({ options, selected, setSelected, label }) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });

  const allSelected = selected.length === options.length;

  const toggleOption = (opt) => {
    setSelected(
      selected.some((item) => item.id === opt.id)
        ? selected.filter((o) => o.id !== opt.id)
        : [...selected, opt]
    );
  };

  const toggleAll = () => {
    setSelected(allSelected ? [] : [...options]);
  };

  useEffect(() => {
    if (open) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, [open]);

  return (
    <>
      <div
        ref={buttonRef}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="border rounded px-2 py-1 bg-white flex items-center cursor-pointer min-h-[32px]"
      >
        <span className="flex-1 flex items-center">
          {selected.length > 0 ? (
            <>
              <span className="bg-gray-100 max-w-[120px] whitespace-nowrap text-ellipsis overflow-hidden rounded-md px-1 py-0.5">
                {selected[0].name}
              </span>
              {selected.length > 1 && (
                <span className="bg-gray-100 text-gray-700 px-1 py-0.5 rounded-md ml-1">
                  +{selected.length - 1}
                </span>
              )}
            </>
          ) : (
            <span className="text-gray-400">{label}</span>
          )}
        </span>
        <svg
          className="w-4 h-4 text-gray-400"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
            clipRule="evenodd"
          />
        </svg>
      </div>

      {open &&
        ReactDOM.createPortal(
          <div
            ref={dropdownRef}
            onClick={(e) => e.stopPropagation()}
            className="dropdown-portal bg-white border border-gray-200 rounded-lg shadow-lg p-2 z-[9999]"
            style={{
              position: "absolute",
              top: position.top,
              left: position.left,
              width: position.width,
            }}
          >
            <label className="flex items-center px-2 py-1 cursor-pointer font-medium text-gray-700">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleAll}
                className="mr-2"
              />
              Select all ({options.length})
            </label>
            <div className="divide-y max-h-48 overflow-y-auto mt-1">
              {options.map((opt) => (
                <label
                  key={opt.id}
                  className="flex items-center px-2 py-1 cursor-pointer text-gray-700"
                >
                  <input
                    type="checkbox"
                    checked={selected.some((item) => item.id === opt.id)}
                    onChange={() => toggleOption(opt)}
                    className="mr-2"
                  />
                  {opt.name}
                </label>
              ))}
            </div>
          </div>,
          document.getElementById("dropdown-root")
        )}
    </>
  );
}

// Main FilterPopover component
function FilterPopover({ locations, filters, onFilterChange }) {
  const [open, setOpen] = useState(false);
  const popoverRef = useRef(null);

  const handleChange = (key, value) => {
    onFilterChange({ ...filters, [key]: value });
  };

  useEffect(() => {
    function handleClickOutside(event) {
      const clickedInsidePopover =
        popoverRef.current && popoverRef.current.contains(event.target);
      const clickedInDropdown =
        event.target.closest(".dropdown-portal") !== null;

      if (!clickedInsidePopover && !clickedInDropdown) {
        setOpen(false);
      }
    }

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  return (
    <div className="relative inline-block" ref={popoverRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="px-4 py-2 bg-white border rounded-lg shadow-sm flex items-center gap-2 hover:bg-gray-100 text-gray-800"
      >
        <span className="font-medium">
          Filters ({filters.leaveTypes.length})
        </span>
      </button>

      {open && (
        <div className="absolute left-0 mt-2 w-[400px] bg-white border p-4 rounded-xl shadow-lg z-40 space-y-4">
          {/* Status */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              className="w-full border rounded px-2 py-1 text-sm"
              value={filters.status}
              onChange={(e) => handleChange("status", e.target.value)}
            >
              <option value="all">All</option>
              <option value="employed">Employed</option>
              <option value="archived">Archived</option>
            </select>
          </div>

          {/* Location */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Location
            </label>
            <select
              className="w-full border rounded px-2 py-1 text-sm"
              value={filters.location}
              onChange={(e) => handleChange("location", e.target.value)}
            >
              <option value="All">All</option>
              {locations.map((loc) => (
                <option value={loc.id}>{loc.name}</option>
              ))}
            </select>
          </div>

          {/* Payroll ID */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Payroll ID
            </label>
            <select
              className="w-full border rounded px-2 py-1 text-sm"
              value={filters.payrollId}
              onChange={(e) => handleChange("payrollId", e.target.value)}
            >
              <option value="all">All</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>

          {/* Leave Type */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Leave Types
            </label>
            <MultiSelectDropdown
              options={leaveTypeOptions}
              selected={filters.leaveTypes}
              setSelected={(val) => handleChange("leaveTypes", val)}
              label="Select Leave Types"
            />
          </div>

          {/* Access */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Access
            </label>
            <select
              className="w-full border rounded px-2 py-1 text-sm"
              value={filters.access}
              onChange={(e) => handleChange("access", e.target.value)}
            >
              <option value="all">All</option>
              <option value="system_administrator">System Administrator</option>
              <option value="location_manager">Location Manager</option>
            </select>
          </div>

          {/* Stress Profile */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Stress Profile
            </label>
            <select
              className="w-full border rounded px-2 py-1 text-sm"
              value={filters.stress}
              onChange={(e) => handleChange("stress", e.target.value)}
            >
              <option value="all">All</option>
              <option value="2_days_per_week">2 days per week</option>
              <option value="24_7">24/7</option>
              <option value="overtime_40">CA Overtime 40hrs/week</option>
              <option value="max_20_hours">Max 20 hours/week</option>
              <option value="normal_38_hours_per_week">
                Normal 38 hours/week
              </option>
              <option value="standard_40">Standard 40 hours</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );
}

export default FilterPopover;
